import { useState } from 'react';
import {
  Row,
  Col,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  Button,
} from 'reactstrap';
import Flatpickr from 'react-flatpickr';
import { useSelector } from 'react-redux';

const DynamicFilter = ({
  filters = [],
  sortByOptions = [],
  onFilterChange,
}) => {
  const { brands, countries, stores } = useSelector(
    (state) => state?.organization
  );

  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [dateRange, setDateRange] = useState('');
  const [sortBy, setSortBy] = useState(null);

  const countryOptions =
    countries?.map((country) => ({
      value: country.countryCode,
      label: `${country?.flag} ${country.name}`,
    })) || [];

  const brandsOptions =
    brands?.map((brand) => ({
      value: brand?.brandId,
      label: brand?.name,
    })) || [];

  const storesOptions =
    stores?.map((store) => ({
      value: store?.storeId,
      label: store?.name,
    })) || [];

  const filterMap = filters.reduce((acc, f) => {
    acc[f.filterColumn] = f;
    return acc;
  }, {});

  const handleAddFilter = (filter) => {
    if (!selectedFilters.includes(filter.filterColumn)) {
      setSelectedFilters([...selectedFilters, filter.filterColumn]);
    }
  };

  const handleValueChange = (
    filterColumn,
    value,
    checked,
    isCustomField = false
  ) => {
    try {
      setFilterValues((prev) => {
        const currentValues = isCustomField
          ? prev.customFields?.[filterColumn] || []
          : prev[filterColumn] || [];

        const newValues = checked
          ? [...currentValues, value]
          : currentValues.filter((v) => v !== value);

        const updated = { ...prev };

        if (isCustomField) {
          updated.customFields = {
            ...(prev.customFields || {}),
            [filterColumn]: newValues.length > 0 ? newValues : undefined,
          };
        } else {
          updated[filterColumn] = newValues.length > 0 ? newValues : undefined;
        }

        onFilterChange({
          sortBy,
          filters: updated,
        });

        return updated;
      });
    } catch (e) {
      console.log('Error updating filter values:', e);
    }
  };

  const handleDateRangeChange = (column, selectedDates, isCustomField) => {
    try {
      const [sDate, eDate] = selectedDates || [];
      const formattedStart = sDate?.toISOString().split('T')[0];
      const formattedEnd = eDate?.toISOString().split('T')[0];
      setDateRange(
        formattedStart && formattedEnd
          ? `${formattedStart} to ${formattedEnd}`
          : ''
      );

      setFilterValues((prev) => {
        const updated = { ...prev };

        const rangeValue =
          sDate && eDate ? { from: formattedStart, to: formattedEnd } : null;

        if (isCustomField) {
          updated.customFields = {
            ...(prev.customFields || {}),
            [column]: rangeValue,
          };
        } else {
          updated[column] = rangeValue;
        }

        onFilterChange({
          sortBy,
          filters: updated,
        });

        return updated;
      });
    } catch (e) {
      console.log(e);
    }
  };

  const resetFilter = () => {
    setSelectedFilters([]);
    setFilterValues({});
    onFilterChange({
      filters: {},
    });
  };

  return (
    <Row>
      <Col md={12}>
        <div className="listSearch border p-1 pe-5 rounded-3 position-relative">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            {selectedFilters.map((filterKey) => {
              const filter = filterMap[filterKey];
              if (!filter) return null;

              if (filter?.label === 'Country') {
                filter.options = countryOptions;
              }

              if (filter?.label === 'Brands') {
                filter.options = brandsOptions;
              }

              if (filter?.label === 'Stores') {
                filter.options = storesOptions;
              }

              const currentFilterValue = filter.isCustomField
                ? filterValues?.customFields?.[filter.filterColumn]
                : filterValues?.[filter.filterColumn];

              if (filter.type === 'dateRange') {
                return (
                  <div
                    className="listSearchContain bg-light rounded-3 lh-1 d-flex"
                    key="date"
                  >
                    <span className="px-2 fs-13 fw-medium d-flex align-items-center">
                      Date
                    </span>
                    <Flatpickr
                      className="border-0 form-control fs-13 p-0 bg-info text-white rounded-0 rounded-end-3 px-2 py-2"
                      options={{
                        mode: 'range',
                        dateFormat: 'Y-m-d',
                        onClose(dates) {
                          handleDateRangeChange(
                            filter.filterColumn,
                            dates,
                            filter.isCustomField
                          );
                        },
                      }}
                      value={dateRange}
                      placeholder="YYYY-MM-DD to YYYY-MM-DD"
                      style={{ width: 200 }}
                    />
                  </div>
                );
              }

              if (filter.type === 'boolean') {
                return (
                  <div
                    className="listSearchContain bg-light rounded-3 lh-1 d-flex"
                    key={filter.filterColumn}
                  >
                    <span className="px-2 fs-13 fw-medium d-flex align-items-center">
                      {filter.label}
                    </span>
                    <div className="bg-info text-white position-relative rounded-end-3">
                      <select
                        className="border-0 form-control fs-13 p-0 bg-info text-white rounded-0 rounded-end-3 px-2 pe-4 py-2"
                        aria-label="Default select example"
                        value={
                          filter.isCustomField
                            ? filterValues?.customFields?.[
                                filter.filterColumn
                              ] ?? ''
                            : filterValues?.[filter.filterColumn] ?? ''
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          const parsedVal =
                            val === '' ? null : val === 'true' ? true : false;

                          const updated = { ...filterValues };
                          if (filter.isCustomField) {
                            updated.customFields = {
                              ...(filterValues.customFields || {}),
                              [filter.filterColumn]: parsedVal,
                            };
                          } else {
                            updated[filter.filterColumn] = parsedVal;
                          }

                          setFilterValues(updated);
                          onFilterChange({
                            sortBy,
                            filters: updated,
                          });
                        }}
                      >
                        <option value="">Any</option>
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                      <i className="bx bx-chevron-down fs-18 position-absolute top-50 end-0 translate-middle-y me-2"></i>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  className="listSearchContain bg-light rounded-3 lh-1 d-flex"
                  key={filter.filterColumn}
                >
                  <span className="px-2 fs-13 fw-medium d-flex align-items-center">
                    {filter.label}
                  </span>
                  <UncontrolledDropdown group>
                    <DropdownToggle
                      size="sm"
                      color="info"
                      className="shadow-none d-flex justify-content-center align-items-center rounded-0 rounded-end-3 px-3 py-2 fs-13"
                    >
                      {(currentFilterValue || [])
                        .map(
                          (val) =>
                            filter.options.find((opt) => opt.value === val)
                              ?.label
                        )
                        .filter(Boolean)
                        .join(', ') || 'Any'}
                      <i className="bx bx-chevron-down fs-18"></i>
                    </DropdownToggle>
                    <DropdownMenu className="px-2 py-3">
                      {filter.options?.map((opt) => (
                        <div className="form-check mb-1 p-1" key={opt.value}>
                          <label className="form-check-label fs-14 fw-normal d-flex gap-2">
                            <input
                              type="checkbox"
                              className="form-check-input m-0"
                              value={opt.value}
                              checked={
                                currentFilterValue?.includes(opt.value) || false
                              }
                              onChange={(e) =>
                                handleValueChange(
                                  filter.filterColumn,
                                  opt.value,
                                  e.target.checked,
                                  filter.isCustomField
                                )
                              }
                            />
                            <span className="text-nowrap">{opt.label}</span>
                          </label>
                        </div>
                      ))}
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              );
            })}

            <div className="listSearchContain bg-light rounded-3 lh-1 d-flex">
              <span className="px-2 fs-13 fw-medium d-flex align-items-center">
                Sort By
              </span>
              <div className="bg-info text-white position-relative rounded-end-3">
                <select
                  className="border-0 form-control fs-13 p-0 bg-info text-white rounded-0 rounded-end-3 px-2 pe-4 py-2"
                  value={JSON.stringify(sortBy)}
                  onChange={(e) => {
                    const selected = JSON.parse(e.target.value);
                    setSortBy(selected);
                    onFilterChange({
                      sortBy: selected,
                      filters: {
                        ...filterValues,
                      },
                    });
                  }}
                >
                  {sortByOptions?.map((option) => (
                    <option
                      key={option?.value?.key}
                      value={JSON.stringify(option?.value)}
                    >
                      {option.label}
                    </option>
                  ))}
                </select>

                <i className="bx bx-chevron-down fs-18 position-absolute top-50 end-0 translate-middle-y me-2"></i>
              </div>
            </div>

            {/* SEARCH */}
            <Input
              className="border-0 form-control fs-13 w-auto px-0"
              placeholder="Search by keyword..."
              type="text"
              style={{ minWidth: 300 }}
              value={filterValues?.searchKey || ''}
              onChange={(e) => {
                const keyword = e?.target?.value;
                const updated = {
                  ...filterValues,
                  searchKey: keyword,
                };
                setFilterValues(updated);
                onFilterChange({
                  sortBy,
                  filters: updated,
                });
              }}
            />
          </div>
          <div className="position-absolute top-0 end-0 m-1 p-2">
            <i className="ri-filter-3-line"></i>
          </div>
        </div>

        {/* ADD FILTER BUTTONS */}
        <ul className="list-unstyled d-flex flex-row align-items-center gap-2 fs-13 mt-2">
          {filters
            .filter((f) => !selectedFilters.includes(f.filterColumn))
            .map((f) => (
              <li
                key={f.filterColumn}
                className="border border-dashed px-2 py-1 rounded-5 lh-1 cursor-pointer"
                onClick={() => handleAddFilter(f)}
              >
                <i className="ri-add-circle-line"></i> {f.label}
              </li>
            ))}
          <li>
            <Button
              color="light"
              outline
              size="sm"
              className="shadow-none d-flex align-items-center justify-content-center border-dark-subtle border-0"
              onClick={() => resetFilter()}
            >
              <i className="mdi mdi-restore fs-18 text-dark"></i>
            </Button>
          </li>
        </ul>
      </Col>
    </Row>
  );
};

export default DynamicFilter;
